import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Typography,
  useTheme,
} from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';

import { ALLOW_CINEMA_DETAILS_PREFETCH } from '@/constants/prefetch';
import { FC } from 'react';
import IconLocation from './icon-location';
import ImageBoxFuture from './image-box-future';
import Link from 'next/link';
import { pxToRem } from '@/theme/typography';
import { useCity } from '@/logic/hooks';

interface cardProps {
  image: string;
  name?: string;
  alt?: string;
  logoAlt?: string;
  cityName?: string;
  logo?: string;
  description: string;
  slug: string;
  address1?: string;
  linkName: string;
}

export const CardComponent: FC<cardProps> = cardProps => {
  const theme = useTheme();
  const intl = useIntl();
  const { cityId: browserCityId } = useCity();

  return (
    <Card
      component='article'
      sx={{
        background: theme.palette.layout.bgSecondary,
        boxShadow: theme.customShadows.cardShadow,
        borderRadius: '25px',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <ImageBoxFuture
        img={cardProps?.image}
        title={cardProps?.alt || cardProps?.name}
        layout='intrinsic'
        objectFit='cover'
        width='524px'
        height='294px'
        sx={{
          borderRadius: '20px',
          overflow: 'hidden',
        }}
      />

      <CardContent sx={{ p: pxToRem(24) }}>
        <Typography
          gutterBottom
          component='h3'
          sx={{
            fontSize: pxToRem(28),
            mb: pxToRem(16),
            fontWeight: 700,
            display: '-webkit-box',
            WebkitLineClamp: '1',
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {cardProps?.name}
        </Typography>

        {cardProps.logo && (
          <ImageBoxFuture
            img={cardProps?.logo}
            title={`${cardProps?.logoAlt || cardProps.name}${intl.formatMessage({
              id: 'images.logo',
            })}`}
            layout='intrinsic'
            objectFit='contain'
            width='794px'
            height='397px'
            sx={{
              marginBottom: pxToRem(24),
              width: {
                mobile: '184px',
                tablet: pxToRem(283),
              },
            }}
          />
        )}
        {cardProps.cityName && (
          <Box component='div' sx={{ display: 'flex', mb: pxToRem(24) }}>
            <IconLocation />
            <Typography
              gutterBottom
              component='address'
              sx={{
                fontSize: pxToRem(24),
                mx: pxToRem(12),

                display: '-webkit-box',
                WebkitLineClamp: '1',
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {cardProps.address1}
            </Typography>
          </Box>
        )}
        <Typography
          variant='body1'
          component='summary'
          color='text.secondary'
          sx={{
            fontSize: pxToRem(24),
            color: theme.palette.grey[50],
            display: '-webkit-box',
            WebkitLineClamp: '4',
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {cardProps.description}
        </Typography>
      </CardContent>
      <CardActions
        sx={{
          justifyContent: 'center',
          mb: pxToRem(24),
          p: 0,
          mt: 'auto',
        }}
      >
        <Link
          href={{
            pathname: `/${cardProps.linkName}/${cardProps.slug}`,
            // Omit cityId when unset instead of emitting `?cityId=`.
            query: { ...(browserCityId && { cityId: browserCityId }) },
          }}
          legacyBehavior
        passHref
          prefetch={ALLOW_CINEMA_DETAILS_PREFETCH}
        >
          <Button
            variant='contained'
            sx={{
              fontSize: pxToRem(16),
              width: '178px',
              height: { tablet: pxToRem(56), mobile: '56px' },
              borderRadius: '16px',
            }}
          >
            <FormattedMessage id='global.explore' />
          </Button>
        </Link>
      </CardActions>
    </Card>
  );
};

export default CardComponent;
